/**
 * User Detail Page — Super Admin manages a single user
 * Tabs: General | Residual Setup | Bonuses
 * Accessed via /admin/users/:id
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, User, Percent, DollarSign, ShieldCheck,
  AlertTriangle, ChevronRight
} from 'lucide-react';

const roleLabels = { super_admin: 'Super Admin', iso: 'ISO', iso_agent: 'ISO Agent', iso_subagent: 'ISO Subagent' };

const RISK_CATEGORIES = [
  { key: 'low_risk_eligible', label: 'Low Risk Eligible Split', default: 70 },
  { key: 'low_risk_non_eligible', label: 'Low Risk Non Eligible Split', default: 70 },
  { key: 'moderate_risk', label: 'Moderate Risk Split', default: 60 },
  { key: 'high_risk', label: 'High Risk Split', default: 40 },
];

export default function UserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);

  // General form
  const [general, setGeneral] = useState({});

  // Risk splits form
  const [riskSplits, setRiskSplits] = useState({});

  // Bonus form
  const [bonusDialog, setBonusDialog] = useState(false);
  const [bonusForm, setBonusForm] = useState({ amount: '', description: '', period: '', sa_password: '' });

  const loadUser = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.request(`/super-admin/users/${userId}`);
      setUser(data);
      setGeneral({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        phone: data.phone || '',
        role: data.role || 'iso_agent',
      });
      // Initialize risk splits
      const rs = data.risk_splits || {};
      const splits = {};
      RISK_CATEGORIES.forEach(c => {
        splits[c.key] = rs[c.key]?.percentage ?? c.default;
        splits[`${c.key}_true_split`] = rs[c.key]?.true_split ?? false;
      });
      setRiskSplits(splits);
    } catch (err) {
      toast.error('Failed to load user');
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => { loadUser(); }, [loadUser]);

  const saveGeneral = async () => {
    setSaving(true);
    try {
      await api.request(`/super-admin/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(general),
      });
      toast.success('User updated');
      loadUser();
    } catch (err) {
      toast.error(err.message);
    }
    setSaving(false);
  };

  const saveRiskSplits = async () => {
    setSaving(true);
    try {
      await api.request(`/super-admin/users/${userId}/risk-splits`, {
        method: 'PUT',
        body: JSON.stringify(riskSplits),
      });
      toast.success('Splits updated');
      loadUser();
    } catch (err) {
      toast.error(err.message);
    }
    setSaving(false);
  };

  const createBonus = async () => {
    if (!bonusForm.amount || !bonusForm.sa_password) {
      toast.error('Amount and admin password are required');
      return;
    }
    setSaving(true);
    try {
      await api.request(`/super-admin/users/${userId}/bonuses`, {
        method: 'POST',
        body: JSON.stringify({
          amount: parseFloat(bonusForm.amount),
          description: bonusForm.description,
          period: bonusForm.period,
          sa_password: bonusForm.sa_password,
        }),
      });
      toast.success('Bonus created');
      setBonusDialog(false);
      setBonusForm({ amount: '', description: '', period: '', sa_password: '' });
      loadUser();
    } catch (err) {
      toast.error(err.message || 'Failed — check admin password');
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div>;
  }

  if (!user) {
    return <div className="text-center py-12 text-muted-foreground">User not found</div>;
  }

  const tabs = [
    { key: 'general', label: 'General', icon: User },
    { key: 'residual', label: 'Residual Setup', icon: Percent },
    { key: 'bonuses', label: 'Bonuses', icon: DollarSign },
  ];

  return (
    <div className="space-y-6" data-testid="user-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/users')} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-lg font-bold text-primary">
              {(user.first_name?.[0] || '').toUpperCase()}{(user.last_name?.[0] || '').toUpperCase()}
            </div>
            <div>
              <h1 className="text-xl font-bold" data-testid="user-name">{user.first_name} {user.last_name}</h1>
              <p className="text-sm text-muted-foreground">{user.email} — <span className="capitalize">{roleLabels[user.role] || user.role}</span> — {user.tenant_name}</p>
            </div>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {user.status}
        </span>
      </div>

      {/* Tabs */}
      <div className="flex border-b">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === t.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`tab-${t.key}`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* === General Tab === */}
      {activeTab === 'general' && (
        <div className="bg-card border rounded-xl p-6 space-y-5">
          <h2 className="font-semibold">Profile Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>First Name</Label>
              <Input value={general.first_name} onChange={e => setGeneral(p => ({ ...p, first_name: e.target.value }))} className="mt-1" data-testid="edit-first-name" />
            </div>
            <div>
              <Label>Last Name</Label>
              <Input value={general.last_name} onChange={e => setGeneral(p => ({ ...p, last_name: e.target.value }))} className="mt-1" data-testid="edit-last-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={user.email} disabled className="mt-1 bg-muted" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={general.phone} onChange={e => setGeneral(p => ({ ...p, phone: e.target.value }))} className="mt-1" data-testid="edit-phone" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={general.role} onValueChange={v => setGeneral(p => ({ ...p, role: v }))}>
                <SelectTrigger className="mt-1" data-testid="edit-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="iso">ISO</SelectItem>
                  <SelectItem value="iso_agent">ISO Agent</SelectItem>
                  <SelectItem value="iso_subagent">ISO Subagent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Input value={user.status} disabled className="mt-1 bg-muted capitalize" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveGeneral} disabled={saving} data-testid="save-general-btn">
              <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      )}

      {/* === Residual Setup Tab === */}
      {activeTab === 'residual' && (
        <div className="space-y-6">
          {/* Risk-Based Splits */}
          <div className="bg-card border rounded-xl p-6 space-y-5">
            <div className="flex items-center gap-2">
              <Percent className="h-5 w-5 text-primary" />
              <h2 className="font-semibold">Risk-Based Splits</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              Configure how much of your revenue share is passed to this user, broken down by merchant risk category.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {RISK_CATEGORIES.map(cat => (
                <div key={cat.key} className="bg-muted/30 rounded-xl p-4 space-y-2">
                  <Label className="text-sm font-semibold">{cat.label}</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={riskSplits[cat.key] ?? cat.default}
                      onChange={e => setRiskSplits(p => ({ ...p, [cat.key]: parseFloat(e.target.value) || 0 }))}
                      className="w-24"
                      data-testid={`split-${cat.key}`}
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                    <div className="flex-1" />
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={riskSplits[`${cat.key}_true_split`] ?? false}
                        onChange={e => setRiskSplits(p => ({ ...p, [`${cat.key}_true_split`]: e.target.checked }))}
                        className="accent-primary"
                      />
                      <span className="text-xs font-medium text-muted-foreground">True Split</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>

            {/* Warning */}
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                Keep in mind that changes in the splits might affect percentages downstream. Make sure to verify all users in the chain.
              </p>
            </div>

            <div className="flex justify-end">
              <Button onClick={saveRiskSplits} disabled={saving} data-testid="save-splits-btn">
                <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Splits'}
              </Button>
            </div>
          </div>

          {/* Downline Users */}
          {user.downline_splits && user.downline_splits.length > 0 && (
            <div className="bg-card border rounded-xl p-6 space-y-4">
              <h2 className="font-semibold">Downline Users</h2>
              <p className="text-xs text-muted-foreground">Users that {user.first_name} has configured splits for</p>
              <div className="space-y-2">
                {user.downline_splits.map(d => (
                  <div key={d.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                        {(d.first_name?.[0] || '?').toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{d.first_name} {d.last_name}</p>
                        <p className="text-xs text-muted-foreground">{d.email} — {roleLabels[d.role] || d.role}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-green-700 bg-green-50 px-2 py-1 rounded-full">
                        {d.split_percentage}%
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/users/${d.id}`)} data-testid={`go-to-${d.id}`}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* === Bonuses Tab === */}
      {activeTab === 'bonuses' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-primary" /> Upfront Bonuses
              </h2>
              <p className="text-sm text-muted-foreground">Requires Super Admin password authentication to place a bonus.</p>
            </div>
            <Button onClick={() => setBonusDialog(true)} data-testid="add-bonus-btn">
              <DollarSign className="h-4 w-4 mr-2" /> Add Bonus
            </Button>
          </div>

          {/* Total bonuses stat */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-card border rounded-xl p-5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Total Bonuses Paid</p>
              <p className="text-2xl font-bold text-green-700 mt-1" data-testid="total-bonuses">
                ${(user.bonuses || []).reduce((s, b) => s + (b.amount || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="bg-card border rounded-xl p-5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Bonus Count</p>
              <p className="text-2xl font-bold mt-1">{(user.bonuses || []).length}</p>
            </div>
            <div className="bg-card border rounded-xl p-5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Last Bonus</p>
              <p className="text-2xl font-bold mt-1">
                {user.bonuses?.length > 0 ? new Date(user.bonuses[0].created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>

          {/* Bonus table */}
          <div className="bg-card border rounded-xl overflow-hidden">
            <table className="w-full" data-testid="bonuses-table">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Amount</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Description</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Period</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {(user.bonuses || []).length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-12 text-muted-foreground">No bonuses yet</td></tr>
                ) : (user.bonuses || []).map((b, idx) => (
                  <tr key={b.id || idx} className="border-b last:border-0 hover:bg-muted/20">
                    <td className="py-3 px-4 text-sm">{new Date(b.created_at).toLocaleDateString()}</td>
                    <td className="py-3 px-4 text-sm font-bold text-green-700">${b.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="py-3 px-4 text-sm">{b.description || '-'}</td>
                    <td className="py-3 px-4 text-sm">{b.period || '-'}</td>
                    <td className="py-3 px-4">
                      <span className="text-xs px-2 py-1 rounded-full bg-green-50 text-green-700 font-medium">{b.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bonus Dialog */}
      <Dialog open={bonusDialog} onOpenChange={setBonusDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Add Bonus — Secure Action
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Bonus Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={bonusForm.amount}
                onChange={e => setBonusForm(p => ({ ...p, amount: e.target.value }))}
                className="mt-1"
                placeholder="500.00"
                data-testid="bonus-amount-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={bonusForm.description}
                onChange={e => setBonusForm(p => ({ ...p, description: e.target.value }))}
                className="mt-1"
                placeholder="e.g. Sign-up bonus, Performance bonus"
                data-testid="bonus-description-input"
              />
            </div>
            <div>
              <Label>Period</Label>
              <Input
                value={bonusForm.period}
                onChange={e => setBonusForm(p => ({ ...p, period: e.target.value }))}
                className="mt-1"
                placeholder="e.g. 2026-02"
                data-testid="bonus-period-input"
              />
            </div>
            <div className="border-t pt-4">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="h-4 w-4 text-amber-500" />
                <Label className="text-amber-700 font-semibold">Super Admin Password Required</Label>
              </div>
              <Input
                type="password"
                value={bonusForm.sa_password}
                onChange={e => setBonusForm(p => ({ ...p, sa_password: e.target.value }))}
                className="mt-1"
                placeholder="Enter your admin password to authorize"
                data-testid="bonus-sa-password"
              />
              <p className="text-xs text-muted-foreground mt-1">This ensures only authorized admins can place bonuses.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBonusDialog(false)}>Cancel</Button>
            <Button onClick={createBonus} disabled={saving} data-testid="confirm-bonus-btn">
              {saving ? 'Processing...' : 'Authorize & Create Bonus'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
